import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Container,
  Row,
  Col,
  Card,
  Form,
  Button,
  Tabs,
  Tab,
  Table,
  Alert,
  Badge,
} from "react-bootstrap";
import { Line } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import { LinkedAlgo, AssetClass, Symbol } from "../types";
import axios from "axios";
import { getCSRFToken } from "../utils/csrf";
import { Trash2, Square, Bell, X } from "lucide-react";
import PerformanceChart from "../components/PerformanceChart";

// Register ChartJS components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
);

const LinkedAlgoEdit: React.FC = () => {
  const { instanceId, linkedAlgoId } = useParams<{
    instanceId: string;
    linkedAlgoId: string;
  }>();
  const navigate = useNavigate();
  const [linkedAlgo, setLinkedAlgo] = useState<LinkedAlgo | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState("performance");
  const [notifications, setNotifications] = useState<any[]>([]);
  const [trades, setTrades] = useState<any[]>([]);
  const [notificationFilter, setNotificationFilter] = useState<string>("all");
  const [showNotifications, setShowNotifications] = useState(true);

  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    fetchLinkedAlgoDetails();
  }, [instanceId, linkedAlgoId]);

  const fetchLinkedAlgoDetails = async () => {
    try {
      setLoading(true);
      const response = await axios.get(
        `${BACKEND_URL}/api/instances/${instanceId}/linked-algos/${linkedAlgoId}`
      );
      setLinkedAlgo({
        ...response.data,
        parameters: {
          ...response.data.parameters,
          risk: {
            closeTradeAfterDrawdown: 0,
            maxPositionDuration: 0,
            ...response.data.parameters.risk,
          },
        },
      });

      // Fetch notifications and trades
      const [notificationsRes, tradesRes] = await Promise.all([
        axios.get(
          `${BACKEND_URL}/api/instances/${instanceId}/linked-algos/${linkedAlgoId}/notifications`
        ),
        axios.get(
          `${BACKEND_URL}/api/instances/${instanceId}/linked-algos/${linkedAlgoId}/trades`
        ),
      ]);

      setNotifications(notificationsRes.data.notifications || []);
      setTrades(tradesRes.data.trades || []);
    } catch (error) {
      console.error("Error fetching linked algo details:", error);
      setError("Failed to load linked algo details");
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!linkedAlgo) return;

    try {
      const csrfToken = await getCSRFToken();
      const response = await axios.put(
        `${BACKEND_URL}/api/instances/${instanceId}/linked-algos/${linkedAlgoId}`,
        {
          ...linkedAlgo,
          parameters: {
            ...linkedAlgo.parameters,
            symbols: linkedAlgo.parameters.symbols.map((symbol) => ({
              ...symbol,
              maxCapital: Number(symbol.maxCapital),
              assetClass:
                symbol.assetClass ||
                linkedAlgo.parameters.assetClasses?.[0]?.type ||
                "Stocks",
            })),
            assetClasses: linkedAlgo.parameters.assetClasses.map((ac) => ({
              type: ac.type,
              config: ac.config,
            })),
          },
        },
        {
          headers: {
            "Content-Type": "application/json",
            "X-CSRFToken": csrfToken,
          },
        }
      );

      // Update local state with response data
      setLinkedAlgo(response.data);
      setError(null);

      // Show success message
      alert("Changes saved successfully!");
    } catch (error) {
      console.error("Error saving linked algo:", error);
      setError("Failed to save changes");
    }
  };

  const handleDelete = async () => {
    if (window.confirm("Are you sure you want to delete this algorithm?")) {
      try {
        const csrfToken = await getCSRFToken();
        await axios.delete(
          `${BACKEND_URL}/api/instances/${instanceId}/linked-algos/${linkedAlgoId}`,
          {
            headers: {
              "X-CSRFToken": csrfToken,
            },
          }
        );
        navigate(-1); // Go back to previous page after deletion
      } catch (error) {
        console.error("Error deleting linked algo:", error);
        setError("Failed to delete algorithm");
      }
    }
  };

  const handleStopTrading = async () => {
    try {
      const csrfToken = await getCSRFToken();
      await axios.put(
        `${BACKEND_URL}/api/instances/${instanceId}/linked-algos/${linkedAlgoId}`,
        {
          ...linkedAlgo,
          status: "stopped",
        },
        {
          headers: {
            "Content-Type": "application/json",
            "X-CSRFToken": csrfToken,
          },
        }
      );
      // Show success message or update UI
    } catch (error) {
      console.error("Error stopping trading:", error);
      setError("Failed to stop trading");
    }
  };

  const calculatePerformanceMetrics = () => {
    const totalTrades = trades.length;
    // Use net_pnl if available, otherwise use pnl
    const winningTrades = trades.filter((trade) => (trade.net_pnl || trade.pnl || 0) > 0).length;
    const winRate = totalTrades > 0 ? (winningTrades / totalTrades) * 100 : 0;

    // Use net_pnl for performance metrics (includes commissions and fees)
    const netProfit = trades.reduce((sum, trade) => sum + (trade.net_pnl || trade.pnl || 0), 0);

    const profitFactor =
      trades
        .filter((trade) => (trade.net_pnl || trade.pnl || 0) > 0)
        .reduce((sum, trade) => sum + (trade.net_pnl || trade.pnl || 0), 0) /
      Math.abs(
        trades
          .filter((trade) => (trade.net_pnl || trade.pnl || 0) < 0)
          .reduce((sum, trade) => sum + (trade.net_pnl || trade.pnl || 0), 0)
      ) || 0;

    // Calculate max drawdown
    let maxDrawdown = 0;
    let peak = linkedAlgo?.cash_at_risk || 0;
    trades.forEach((trade) => {
      const tradePnl = trade.net_pnl || trade.pnl || 0;
      const currentValue = peak + tradePnl;
      if (currentValue > peak) {
        peak = currentValue;
      }
      const drawdown = (peak - currentValue) / peak;
      maxDrawdown = Math.max(maxDrawdown, drawdown);
    });

    // Calculate Sharpe Ratio (assuming risk-free rate of 0.02)
    const returns = trades.map((trade) => trade.net_pnl || trade.pnl || 0);
    const avgReturn =
      returns.reduce((sum, ret) => sum + ret, 0) / returns.length;
    const stdDev = Math.sqrt(
      returns.reduce((sum, ret) => sum + Math.pow(ret - avgReturn, 2), 0) /
      returns.length
    );
    const sharpeRatio = stdDev !== 0 ? (avgReturn - 0.02) / stdDev : 0;

    return {
      netProfit,
      sharpeRatio,
      maxDrawdown,
      profitFactor,
      totalTrades,
      winRate,
    };
  };

  // Helper function to render config field (mirrors ConfigurationPanel logic)
  const renderConfigField = (
    key: string,
    value: any,
    onChange: (value: any) => void,
    componentType?: string
  ) => {
    // Convert key to display format (e.g., "maxCapital" -> "Max Capital")
    const displayKey = key
      .replace(/([A-Z])/g, " $1")
      .replace(/^./, (str) => str.toUpperCase());

    // Handle boolean values
    if (typeof value === "boolean") {
      return (
        <Form.Group key={key} className="mb-3">
          <Form.Check
            type="checkbox"
            id={key}
            label={displayKey}
            checked={value}
            onChange={(e) => onChange(e.target.checked)}
          />
        </Form.Group>
      );
    }

    // Handle predefined options (dropdowns)
    const getOptions = (field: string) => {
      switch (field) {
        case "timeframe":
          return [
            { value: "1m", label: "1 Minute" },
            { value: "5m", label: "5 Minutes" },
            { value: "15m", label: "15 Minutes" },
            { value: "30m", label: "30 Minutes" },
            { value: "45m", label: "45 Minutes" },
            { value: "1h", label: "1 Hour" },
            { value: "4h", label: "4 Hours" },
            { value: "1d", label: "1 Day" },
          ];
        case "assetClass":
          return [
            { value: "Stocks", label: "Stocks" },
            { value: "Stock Options", label: "Stock Options" },
            { value: "Futures", label: "Futures" },
            { value: "Futures Options", label: "Futures Options" },
            { value: "Forex", label: "Forex" },
            { value: "Crypto Spot", label: "Crypto Spot" },
            { value: "Crypto Futures", label: "Crypto Futures" },
          ];
        case "riskSource":
          return [
            { value: "Account Balance", label: "Account Balance" },
            { value: "Fixed Amount", label: "Fixed Amount" },
            { value: "Account Equity", label: "Account Equity" },
          ];
        case "lotMode":
          return [
            { value: "Fixed", label: "Fixed" },
            { value: "Risk", label: "Risk" },
            { value: "Percentage", label: "Percentage" },
          ];
        case "trailingStopType":
        case "trailingTakeProfitType":
          return [
            { value: "Percentage", label: "Percentage" },
            { value: "Fixed Amount", label: "Fixed Amount" },
          ];
        case "orderType":
          return [
            { value: "market", label: "Market" },
            { value: "limit", label: "Limit" },
            { value: "stop", label: "Stop" },
            { value: "stop_limit", label: "Stop Limit" },
          ];
        case "allowedTradeDirections":
          return [
            { value: "Long Only", label: "Long Only" },
            { value: "Short Only", label: "Short Only" },
            { value: "Both", label: "Both" },
          ];
        case "maxRiskMode":
          return [
            { value: "percentage", label: "Percentage (%)" },
            { value: "dollar", label: "Dollar Amount ($)" },
          ];
        default:
          return null;
      }
    };

    const options = getOptions(key);
    if (options) {
      return (
        <Form.Group key={key} className="mb-3">
          <Form.Label>{displayKey}</Form.Label>
          <Form.Select value={value || ""} onChange={(e) => onChange(e.target.value)}>
            {options.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </Form.Select>
        </Form.Group>
      );
    }

    // Handle numeric inputs
    if (typeof value === "number") {
      const step =
        key === "lotSize"
          ? 1
          : key === "limitOrderUpdateValue"
            ? 1
            : key.includes("Percentage") || key.includes("percentage")
              ? 0.1
              : 1;

      return (
        <Form.Group key={key} className="mb-3">
          <Form.Label>{displayKey}</Form.Label>
          <Form.Control
            type="number"
            value={value}
            onChange={(e) => onChange(parseFloat(e.target.value) || 0)}
            step={step}
            min={0}
          />
        </Form.Group>
      );
    }

    // Handle time inputs
    if (key.toLowerCase().includes("time")) {
      return (
        <Form.Group key={key} className="mb-3">
          <Form.Label>{displayKey}</Form.Label>
          <Form.Control
            type="time"
            value={value || ""}
            onChange={(e) => onChange(e.target.value)}
          />
        </Form.Group>
      );
    }

    // Default to text input for strings
    const stringValue = value != null ? String(value) : "";
    return (
      <Form.Group key={key} className="mb-3">
        <Form.Label>{displayKey}</Form.Label>
        <Form.Control
          type="text"
          value={stringValue}
          onChange={(e) => onChange(e.target.value)}
        />
      </Form.Group>
    );
  };

  // Get notification priority badge variant
  const getNotificationVariant = (priority: string) => {
    switch (priority?.toUpperCase()) {
      case "CRITICAL":
        return "danger";
      case "HIGH":
        return "warning";
      case "NORMAL":
        return "info";
      case "LOW":
        return "secondary";
      default:
        return "info";
    }
  };

  // Filter notifications
  const filteredNotifications = notifications.filter((notification) => {
    if (notificationFilter === "all") return true;
    return notification.priority?.toUpperCase() === notificationFilter.toUpperCase();
  });

  if (loading) return <div>Loading...</div>;
  if (error) return <div>Error: {error}</div>;
  if (!linkedAlgo) return <div>Linked algo not found</div>;

  const metrics = calculatePerformanceMetrics();

  return (
    <Container fluid className="p-4">
      {/* Header Section */}
      <div className="d-flex justify-content-between align-items-start mb-4">
        <div>
          <h2 className="mb-2">{linkedAlgo.name}</h2>
          <p className="text-muted mb-0">{linkedAlgo.description}</p>
        </div>
        <div className="d-flex gap-2">
          <Button
            variant="outline-danger"
            onClick={handleDelete}
            title="Delete Algorithm"
          >
            <Trash2 size={16} className="me-1" />
            Delete
          </Button>
          <Button
            variant="outline-warning"
            onClick={handleStopTrading}
            title="Stop Trading"
          >
            <Square size={16} className="me-1" />
            Stop Trading
          </Button>
          <Button variant="outline-secondary" onClick={() => navigate(-1)}>
            Back
          </Button>
        </div>
      </div>

      {/* Description and Asset Info Section */}
      <Card className="mb-4">
        <Card.Body>
          <Row>
            <Col md={8}>
              <Form.Group className="mb-3">
                <Form.Label>Description</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={3}
                  value={linkedAlgo.description}
                  onChange={(e) =>
                    setLinkedAlgo({
                      ...linkedAlgo,
                      description: e.target.value,
                    })
                  }
                />
              </Form.Group>
            </Col>
            <Col md={4}>
              <div className="mb-3">
                <h5>Asset Classes</h5>
                <div className="d-flex flex-wrap gap-2">
                  {linkedAlgo.parameters.assetClasses &&
                    linkedAlgo.parameters.assetClasses.map((ac) => (
                      <Badge key={ac.type} bg="primary" className="p-2">
                        {ac.type}
                      </Badge>
                    ))}
                </div>
              </div>
              <div className="mb-3">
                <h5>Symbols</h5>
                <div className="d-flex flex-wrap gap-2">
                  {linkedAlgo.parameters.symbols &&
                    linkedAlgo.parameters.symbols.map((symbol) => (
                      <Badge key={symbol.symbol} bg="secondary" className="p-2">
                        {symbol.symbol}
                      </Badge>
                    ))}
                </div>
              </div>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {/* Balance Section */}
      < Card className="mb-4" >
        <Card.Body>
          <h5>Balance</h5>
          <Row>
            <Col md={3}>
              <div className="text-center">
                <h6>Initial Balance</h6>
                <h4>${linkedAlgo.cash_at_risk.toFixed(2)}</h4>
              </div>
            </Col>
            <Col md={3}>
              <div className="text-center">
                <h6>Current Risk</h6>
                <h4>${linkedAlgo.cash_at_risk.toFixed(2)}</h4>
              </div>
            </Col>
            <Col md={3}>
              <div className="text-center">
                <h6>Current P/L</h6>
                <h4
                  className={
                    linkedAlgo.returns >= 0 ? "text-success" : "text-danger"
                  }
                >
                  ${linkedAlgo.returns.toFixed(2)}
                </h4>
              </div>
            </Col>
            <Col md={3}>
              <div className="text-center">
                <h6>Total Trades</h6>
                <h4>{trades.length}</h4>
              </div>
            </Col>
          </Row>
        </Card.Body>
      </Card >

      {/* Notifications Section - Cleaned Up */}
      < Card className="mb-4" >
        <Card.Header className="d-flex justify-content-between align-items-center">
          <div className="d-flex align-items-center gap-2">
            <Bell size={20} />
            <h5 className="mb-0">Notifications & Alerts</h5>
            <Badge bg="secondary">{notifications.length}</Badge>
          </div>
          <div className="d-flex gap-2 align-items-center">
            <Form.Select
              size="sm"
              style={{ width: "auto" }}
              value={notificationFilter}
              onChange={(e) => setNotificationFilter(e.target.value)}
            >
              <option value="all">All</option>
              <option value="critical">Critical</option>
              <option value="high">High</option>
              <option value="normal">Normal</option>
              <option value="low">Low</option>
            </Form.Select>
            <Button
              variant="link"
              size="sm"
              onClick={() => setShowNotifications(!showNotifications)}
              className="p-0"
            >
              {showNotifications ? "Hide" : "Show"}
            </Button>
          </div>
        </Card.Header>
        {
          showNotifications && (
            <Card.Body>
              {filteredNotifications.length === 0 ? (
                <div className="text-center text-muted py-4">
                  <Bell size={48} className="mb-2 opacity-50" />
                  <p>No notifications found</p>
                </div>
              ) : (
                <div className="notifications-list" style={{ maxHeight: "400px", overflowY: "auto" }}>
                  {filteredNotifications.map((notification) => (
                    <div
                      key={notification.id}
                      className="mb-3 p-3 border rounded"
                      style={{
                        borderLeft: `4px solid ${getNotificationVariant(notification.priority) === "danger"
                          ? "#dc3545"
                          : getNotificationVariant(notification.priority) === "warning"
                            ? "#ffc107"
                            : "#0dcaf0"
                          }`,
                      }}
                    >
                      <div className="d-flex justify-content-between align-items-start mb-2">
                        <div className="flex-grow-1">
                          <div className="d-flex align-items-center gap-2 mb-1">
                            <Badge bg={getNotificationVariant(notification.priority)}>
                              {notification.priority || "NORMAL"}
                            </Badge>
                            {notification.notification_type && (
                              <Badge bg="outline-secondary" className="text-dark">
                                {notification.notification_type}
                              </Badge>
                            )}
                          </div>
                          <p className="mb-1">{notification.message}</p>
                          <small className="text-muted">
                            {new Date(notification.timestamp).toLocaleString()}
                          </small>
                        </div>
                      </div>
                      {notification.tags && notification.tags.length > 0 && (
                        <div className="mt-2">
                          {notification.tags.map((tag: string, idx: number) => (
                            <Badge key={idx} bg="light" text="dark" className="me-1">
                              {tag}
                            </Badge>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </Card.Body>
          )
        }
      </Card >

      {/* Algo Components Section */}
      < Card className="mb-4" >
        <Card.Body>
          <h5>Algo Components</h5>

          {/* Asset Classes Section */}
          <div className="mb-4">
            <div className="d-flex justify-content-between align-items-center mb-3">
              <h6>Asset Classes</h6>
              <Button
                variant="outline-primary"
                size="sm"
                onClick={() => {
                  const newAssetClasses = [
                    ...(linkedAlgo.parameters.assetClasses || []),
                  ];
                  newAssetClasses.push({
                    type: "Stocks",
                    config: {
                      sharesPerTrade: 1,
                      allowedTradeDirections: "Both",
                    },
                  });
                  setLinkedAlgo({
                    ...linkedAlgo,
                    parameters: {
                      ...linkedAlgo.parameters,
                      assetClasses: newAssetClasses,
                    },
                  });
                }}
              >
                Add Asset Class
              </Button>
            </div>
            <Form>
              {linkedAlgo.parameters.assetClasses &&
                linkedAlgo.parameters.assetClasses.map((ac, index) => (
                  <Card key={index} className="mb-3">
                    <Card.Header className="d-flex justify-content-between align-items-center">
                      <h6 className="mb-0">{ac.type}</h6>
                      <Button
                        variant="outline-danger"
                        size="sm"
                        onClick={() => {
                          const newAssetClasses = [
                            ...linkedAlgo.parameters.assetClasses,
                          ];
                          newAssetClasses.splice(index, 1);
                          setLinkedAlgo({
                            ...linkedAlgo,
                            parameters: {
                              ...linkedAlgo.parameters,
                              assetClasses: newAssetClasses,
                            },
                          });
                        }}
                      >
                        <Trash2 size={14} className="me-1" />
                        Remove
                      </Button>
                    </Card.Header>
                    <Card.Body>
                      {renderConfigField(
                        "type",
                        ac.type,
                        (value) => {
                          const newAssetClasses = [
                            ...linkedAlgo.parameters.assetClasses,
                          ];
                          newAssetClasses[index].type = value;
                          setLinkedAlgo({
                            ...linkedAlgo,
                            parameters: {
                              ...linkedAlgo.parameters,
                              assetClasses: newAssetClasses,
                            },
                          });
                        },
                        "AssetClass"
                      )}
                      {Object.entries(ac.config || {}).map(([key, value]) =>
                        renderConfigField(
                          key,
                          value,
                          (newValue) => {
                            const newAssetClasses = [
                              ...linkedAlgo.parameters.assetClasses,
                            ];
                            newAssetClasses[index].config = {
                              ...newAssetClasses[index].config,
                              [key]: newValue,
                            };
                            setLinkedAlgo({
                              ...linkedAlgo,
                              parameters: {
                                ...linkedAlgo.parameters,
                                assetClasses: newAssetClasses,
                              },
                            });
                          },
                          ac.type
                        )
                      )}
                    </Card.Body>
                  </Card>
                ))}
            </Form>
          </div>

          {/* Symbols Section */}
          <div className="mb-4">
            <div className="d-flex justify-content-between align-items-center mb-3">
              <h6>Symbols</h6>
              <Button
                variant="outline-primary"
                size="sm"
                onClick={() => {
                  const newSymbols = [...(linkedAlgo.parameters.symbols || [])];
                  newSymbols.push({
                    symbol: "",
                    maxCapital: 1000,
                    assetClass:
                      linkedAlgo.parameters.assetClasses?.[0]?.type ||
                      "Stocks",
                    takeProfitValue: 0,
                    stopLossValue: 0,
                    maxRiskMode: "percentage",
                    maxRiskValue: 0,
                    lotSize: 1,
                    limitOrderUpdateValue: 50,
                  });
                  setLinkedAlgo({
                    ...linkedAlgo,
                    parameters: {
                      ...linkedAlgo.parameters,
                      symbols: newSymbols,
                    },
                  });
                }}
              >
                Add Symbol
              </Button>
            </div>
            <Form>
              {linkedAlgo.parameters.symbols &&
                linkedAlgo.parameters.symbols.map((symbol, index) => (
                  <Card key={index} className="mb-3">
                    <Card.Header className="d-flex justify-content-between align-items-center">
                      <h6 className="mb-0">Symbol {index + 1}</h6>
                      <Button
                        variant="outline-danger"
                        size="sm"
                        onClick={() => {
                          const newSymbols = [...linkedAlgo.parameters.symbols];
                          newSymbols.splice(index, 1);
                          setLinkedAlgo({
                            ...linkedAlgo,
                            parameters: {
                              ...linkedAlgo.parameters,
                              symbols: newSymbols,
                            },
                          });
                        }}
                      >
                        <Trash2 size={14} className="me-1" />
                        Remove
                      </Button>
                    </Card.Header>
                    <Card.Body>
                      {Object.entries(symbol).map(([key, value]) =>
                        renderConfigField(
                          key,
                          value,
                          (newValue) => {
                            const newSymbols = [...linkedAlgo.parameters.symbols];
                            newSymbols[index] = {
                              ...newSymbols[index],
                              [key]: newValue,
                            };
                            setLinkedAlgo({
                              ...linkedAlgo,
                              parameters: {
                                ...linkedAlgo.parameters,
                                symbols: newSymbols,
                              },
                            });
                          },
                          "Symbol"
                        )
                      )}
                    </Card.Body>
                  </Card>
                ))}
            </Form>
          </div>

          {/* Algorithm Section */}
          {linkedAlgo.parameters.algorithm && (
            <div className="mb-4">
              <h6 className="mb-3">Algorithm</h6>
              <Card>
                <Card.Body>
                  <Form>
                    {Object.entries(linkedAlgo.parameters.algorithm).map(
                      ([key, value]) => {
                        // Skip complex objects and arrays
                        if (
                          typeof value === "object" &&
                          value !== null &&
                          !Array.isArray(value) &&
                          !(value instanceof Date)
                        ) {
                          return null;
                        }
                        if (Array.isArray(value)) {
                          return null;
                        }
                        return renderConfigField(
                          key,
                          value,
                          (newValue) => {
                            setLinkedAlgo({
                              ...linkedAlgo,
                              parameters: {
                                ...linkedAlgo.parameters,
                                algorithm: {
                                  ...linkedAlgo.parameters.algorithm,
                                  [key]: newValue,
                                },
                              },
                            });
                          },
                          "Algorithm"
                        );
                      }
                    )}
                  </Form>
                </Card.Body>
              </Card>
            </div>
          )}

          {/* Risk Section */}
          {linkedAlgo.parameters.risk && (
            <div className="mb-4">
              <h6 className="mb-3">Trade Execution and Management</h6>
              <Card>
                <Card.Body>
                  <Form>
                    {Object.entries(linkedAlgo.parameters.risk).map(
                      ([key, value]) =>
                        renderConfigField(
                          key,
                          value,
                          (newValue) => {
                            setLinkedAlgo({
                              ...linkedAlgo,
                              parameters: {
                                ...linkedAlgo.parameters,
                                risk: {
                                  ...linkedAlgo.parameters.risk,
                                  [key]: newValue,
                                },
                              },
                            });
                          },
                          "Trade Execution and Management"
                        )
                    )}
                  </Form>
                </Card.Body>
              </Card>
            </div>
          )}

          {/* Schedule Section */}
          {linkedAlgo.parameters.schedule && (
            <div className="mb-4">
              <h6 className="mb-3">Schedule</h6>
              <Card>
                <Card.Body>
                  <Form>
                    {Object.entries(linkedAlgo.parameters.schedule).map(
                      ([key, value]) =>
                        renderConfigField(
                          key,
                          value,
                          (newValue) => {
                            setLinkedAlgo({
                              ...linkedAlgo,
                              parameters: {
                                ...linkedAlgo.parameters,
                                schedule: {
                                  ...linkedAlgo.parameters.schedule,
                                  [key]: newValue,
                                },
                              },
                            });
                          },
                          "Schedule"
                        )
                    )}
                  </Form>
                </Card.Body>
              </Card>
            </div>
          )}
        </Card.Body>
      </Card >

      {/* Performance Section */}
      < Card className="mb-4" >
        <Card.Body>
          <h5>Performance</h5>
          <Tabs defaultActiveKey="metrics" className="mb-3">
            <Tab eventKey="metrics" title="Performance Metrics">
              <Row className="g-4">
                <Col md={4}>
                  <div className="p-3 bg-light rounded">
                    <h6 className="text-muted mb-2">Net Profit</h6>
                    <h4
                      className={
                        metrics.netProfit >= 0 ? "text-success" : "text-danger"
                      }
                    >
                      ${metrics.netProfit.toFixed(2)}
                    </h4>
                  </div>
                </Col>
                <Col md={4}>
                  <div className="p-3 bg-light rounded">
                    <h6 className="text-muted mb-2">Sharpe Ratio</h6>
                    <h4>{metrics.sharpeRatio.toFixed(2)}</h4>
                  </div>
                </Col>
                <Col md={4}>
                  <div className="p-3 bg-light rounded">
                    <h6 className="text-muted mb-2">Max Drawdown</h6>
                    <h4 className="text-danger">
                      {(metrics.maxDrawdown * 100).toFixed(1)}%
                    </h4>
                  </div>
                </Col>
                <Col md={4}>
                  <div className="p-3 bg-light rounded">
                    <h6 className="text-muted mb-2">Profit Factor</h6>
                    <h4>{metrics.profitFactor.toFixed(2)}</h4>
                  </div>
                </Col>
                <Col md={4}>
                  <div className="p-3 bg-light rounded">
                    <h6 className="text-muted mb-2">Total Trades</h6>
                    <h4>{metrics.totalTrades}</h4>
                  </div>
                </Col>
                <Col md={4}>
                  <div className="p-3 bg-light rounded">
                    <h6 className="text-muted mb-2">Win Rate</h6>
                    <h4>{metrics.winRate.toFixed(1)}%</h4>
                  </div>
                </Col>
              </Row>
            </Tab>
            <Tab eventKey="history" title="Trade History">
              <Table striped bordered hover responsive>
                <thead>
                  <tr>
                    <th>Date & Time</th>
                    <th>Direction</th>
                    <th>Symbol</th>
                    <th>Entry Price</th>
                    <th>Exit Price</th>
                    <th>Quantity</th>
                    <th>Gross P&L</th>
                    <th>Commissions</th>
                    <th>Fees</th>
                    <th>Net P&L</th>
                    <th>Exit Reason</th>
                    <th>Slippage</th>
                  </tr>
                </thead>
                <tbody>
                  {trades && trades.length > 0 ? (
                    trades.map((trade) => {
                      const entryCommission = trade.entry_commission || 0;
                      const exitCommission = trade.exit_commission || 0;
                      const totalCommissions = entryCommission + exitCommission;
                      const totalFees = trade.total_fees || 0;
                      const netPnl = trade.net_pnl !== undefined ? trade.net_pnl : (trade.pnl || 0);
                      const grossPnl = trade.realized_pnl !== undefined ? trade.realized_pnl : (trade.pnl || 0);

                      return (
                        <tr key={trade.id}>
                          <td>{new Date(trade.date).toLocaleString()}</td>
                          <td>
                            <Badge bg={trade.side === "BUY" ? "success" : "danger"}>
                              {trade.side}
                            </Badge>
                          </td>
                          <td><strong>{trade.symbol}</strong></td>
                          <td>{trade.entry_price ? trade.entry_price.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 8 }) : "N/A"}</td>
                          <td>{trade.exit_price ? trade.exit_price.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 8 }) : "N/A"}</td>
                          <td>{trade.quantity || "N/A"}</td>
                          <td
                            className={
                              grossPnl >= 0 ? "text-success" : "text-danger"
                            }
                          >
                            ${grossPnl.toFixed(2)}
                          </td>
                          <td className="text-muted">
                            ${totalCommissions.toFixed(2)}
                            {totalCommissions > 0 && (
                              <small className="d-block text-muted" style={{ fontSize: "0.75rem" }}>
                                (E: ${entryCommission.toFixed(2)}, X: ${exitCommission.toFixed(2)})
                              </small>
                            )}
                          </td>
                          <td className="text-muted">
                            ${totalFees.toFixed(2)}
                          </td>
                          <td
                            className={
                              netPnl >= 0 ? "text-success fw-bold" : "text-danger fw-bold"
                            }
                          >
                            ${netPnl.toFixed(2)}
                          </td>
                          <td>
                            {trade.exit_reason ? (
                              <Badge bg={
                                trade.exit_reason === "STOP_LOSS" ? "danger" :
                                  trade.exit_reason === "TAKE_PROFIT" ? "success" :
                                    trade.exit_reason === "MANUAL" ? "info" :
                                      "secondary"
                              }>
                                {trade.exit_reason.replace("_", " ")}
                              </Badge>
                            ) : (
                              <span className="text-muted">-</span>
                            )}
                          </td>
                          <td className={trade.slippage && trade.slippage > 0 ? "text-warning" : "text-muted"}>
                            {trade.slippage ? `$${trade.slippage.toFixed(4)}` : "-"}
                          </td>
                        </tr>
                      );
                    })
                  ) : (
                    <tr>
                      <td colSpan={12} className="text-center text-muted">
                        No trades found
                      </td>
                    </tr>
                  )}
                </tbody>
              </Table>
            </Tab>
          </Tabs>
        </Card.Body>
      </Card >

      {/* Save Button */}
      < div className="d-flex justify-content-end mb-4" >
        <Button variant="primary" onClick={handleSave}>
          Save Changes
        </Button>
      </div >
    </Container >
  );
};

export default LinkedAlgoEdit;
